<template>
  <div class="group py-4 first:pt-0">
    <div class="flex items-start justify-between gap-4 mb-3">
      <div class="flex items-center gap-3">
        <div class="flex items-center justify-center w-8 h-8 rounded-lg" :class="actionTypeStyles.bg">
          <vue-feather :type="actionTypeStyles.icon" class="w-4 h-4" :class="actionTypeStyles.text" />
        </div>

        <div v-if="mode === 'new'">
          <FormKit
            label="Action Type"
            name="action_type"
            type="select"
            :options="workflowStore.currentDefinition.actions.map(x => x.name) || []"
            outer-class="w-40 max-w-40 !m-0"
            :model-value="selectedAction.name"
            @update:model-value="changeSelectedAction($event || '')"
          />
        </div>
        <div v-else class="flex items-center gap-2">
          <h3 class="text-sm font-semibold text-foreground tracking-tight">{{ selectedAction.name }}</h3>
          <Tooltip v-if="selectedAction.actionable_type === 'WorkflowActionSms'">
            <TooltipTrigger as-child>
              <vue-feather type="help-circle" class="h-4 w-4 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent>
              <p>Only sent to candidates who opt-in to SMS.</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      <div
        class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
        :class="{ '!opacity-100': mode !== 'view' }"
      >
        <template v-if="mode === 'view'">
          <Button variant="ghost" size="icon" class="h-8 w-8" @click="mode = 'edit'">
            <vue-feather type="edit-2" class="w-4 h-4 text-muted-foreground" />
          </Button>
          <Button variant="ghost" size="icon" class="h-8 w-8 hover:text-destructive" @click="showDeleteModal = true">
            <vue-feather type="trash-2" class="w-4 h-4 text-muted-foreground" />
          </Button>
        </template>
        <template v-else>
          <Button v-if="mode === 'new'" variant="default" size="sm" class="h-8" @click="handleCreateAction">
            <vue-feather type="check" class="w-4 h-4 mr-1" />
            Create
          </Button>
          <Button v-if="mode === 'edit'" variant="default" size="sm" class="h-8" @click="handleUpdateAction">
            <vue-feather type="check" class="w-4 h-4 mr-1" />
            Save
          </Button>
          <Button variant="ghost" size="sm" class="h-8" @click="handleCancel"> Cancel </Button>
        </template>
      </div>
    </div>

    <div class="pl-11">
      <div class="grid grid-cols-2 gap-16">
        <div>
          <p class="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Details</p>
          <component
            :is="selectedActionable.component"
            v-if="selectedAction"
            :ref="selectedActionable.ref"
            :actionable="action?.workflow_actionable"
            :mode="mode"
          />
        </div>

        <div>
          <p class="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Scheduling</p>
          <ActionInvocation
            v-model:delay-in-days="delayInDays"
            :mode="mode"
            :selected-invocation="selectedInvocation"
            @update:selected-invocation="invocation => updateInvocation(invocation)"
          />
        </div>
      </div>
    </div>

    <Modal
      v-if="action && showDeleteModal"
      :title="deleteModal.title"
      :body="deleteModal.description"
      action="Delete"
      action-color="red"
      @close-modal="showDeleteModal = false"
      @handle-action="handleDestroyAction"
    />
  </div>
</template>

<script setup lang="ts">
import type { WorkflowActionableParams } from '@/api/entity_types/workflow/WorkflowAction';
import type { ActionItemMode } from './types.ts';
import { useWorkflowAction } from './useWorkflowAction.ts';
import { useCurrentWorkflowStore } from '@/stores/workflow/currentWorkflow.ts';
import { computed, ref } from 'vue';
import { useActionableComponentMap } from './useActionableComponents.ts';
import Modal from '@/components/shared/Modal.vue';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import ActionInvocation from './ActionInvocation.vue';
import { useActionInvocation } from './useActionInvocation.ts';
import type { GetWorkflowResponseAction } from '@/api/workflows.ts';
import { actionTypeStyle } from '../../actionTypeStyle';

export type ActionItemProps = {
  action?: GetWorkflowResponseAction;
  initMode: ActionItemMode;
};
const { action, initMode } = defineProps<ActionItemProps>();

export type ActionItemEmits = {
  'action-added': [];
  'view-mode-enabled': [];
};
const emit = defineEmits<ActionItemEmits>();

const workflowStore = useCurrentWorkflowStore();
const actionableComponentMap = useActionableComponentMap();
const selectedActionable = computed(() => actionableComponentMap.get(selectedAction.value.actionable_type));
const showDeleteModal = ref(false);
const loadingSpinnerStore = useLoadingSpinnerStore();

const {
  mode,
  selectedAction,
  changeSelectedAction,
  createAction,
  updateAction,
  destroyAction,
  enableViewMode,
  deleteModal,
} = useWorkflowAction({
  action,
  initMode,
  emit,
});

const {
  selectedInvocation,
  delayInDays,
  getInvocationEnum,
  getScheduledActionParams,
  resetInvocation,
  updateInvocation,
} = useActionInvocation({
  action,
});

const buildActionableParams = (): WorkflowActionableParams => {
  const actionableParams: WorkflowActionableParams = selectedActionable.value.ref.value.buildActionableParams();
  if (!actionableParams) throw new Error('Invalid actionable returned');
  return actionableParams;
};

const actionTypeStyles = computed(() => actionTypeStyle(selectedAction.value.actionable_type));

const params = () => ({
  actionParams: { invocation: getInvocationEnum() },
  actionableParams: buildActionableParams(),
  scheduledActionParams: getScheduledActionParams(),
});

const handleCreateAction = async () => await createAction(params());
const handleUpdateAction = async () => await updateAction(params());

const handleCancel = () => {
  resetInvocation(action);
  selectedActionable.value.ref.value.resetFields(action?.workflow_actionable);
  enableViewMode();
};

const handleDestroyAction = async () => {
  await destroyAction();
  loadingSpinnerStore.hideLocalLoadingSpinner();
  showDeleteModal.value = false;
};
</script>
